import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BillingStatusProvider } from "./context/BillingStatusContext";
import { AgentLayout, ExpenseLayout } from "./components/Layout";
import Login from "./pages/Login";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Upload from "./pages/Upload";
import Expenses from "./pages/Expenses";
import Reports from "./pages/Reports";
import Insights from "./pages/Insights";
import Budget from "./pages/Budget";
import Search from "./pages/Search";
import BizCard from "./pages/BizCard";
import Compliance from "./pages/Compliance";
import Admin from "./pages/Admin";
import Employees from "./pages/Employees";
import Payroll from "./pages/Payroll";
import DocumentExpiry from "./pages/DocumentExpiry";
import DataQuality from "./pages/DataQuality";
import HrmsDashboard from "./pages/HrmsDashboard";
import HrmsAssistant from "./pages/HrmsAssistant";
import CompanyDocuments from "./pages/CompanyDocuments";
import BniAdvisor from "./pages/BniAdvisor";

const theme = createTheme({
  palette: { mode: "light", background: { default: "#f0f4fc", paper: "#ffffff" } },
  typography: { fontFamily: "'Inter', sans-serif" },
});

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />

      <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />

      {/* Expense agent */}
      <Route path="/expense" element={<ProtectedRoute><ExpenseLayout><Dashboard /></ExpenseLayout></ProtectedRoute>} />
      <Route path="/expense/upload"   element={<ProtectedRoute><ExpenseLayout><Upload /></ExpenseLayout></ProtectedRoute>} />
      <Route path="/expense/receipts" element={<ProtectedRoute><ExpenseLayout><Expenses /></ExpenseLayout></ProtectedRoute>} />
      <Route path="/expense/reports"  element={<ProtectedRoute><ExpenseLayout><Reports /></ExpenseLayout></ProtectedRoute>} />
      <Route path="/expense/insights" element={<ProtectedRoute><ExpenseLayout><Insights /></ExpenseLayout></ProtectedRoute>} />
      <Route path="/expense/budget"   element={<ProtectedRoute><ExpenseLayout><Budget /></ExpenseLayout></ProtectedRoute>} />
      <Route path="/expense/search"   element={<ProtectedRoute><ExpenseLayout><Search /></ExpenseLayout></ProtectedRoute>} />

      {/* Other agents */}
      <Route path="/bizcard"    element={<ProtectedRoute><AgentLayout><BizCard /></AgentLayout></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><AgentLayout><Compliance /></AgentLayout></ProtectedRoute>} />
      <Route path="/bni"        element={<ProtectedRoute><AgentLayout><BniAdvisor /></AgentLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/hrms/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
      <Route path="/hrms/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
      <Route path="/hrms/expiry" element={<ProtectedRoute><DocumentExpiry /></ProtectedRoute>} />
      <Route path="/hrms/data-quality" element={<ProtectedRoute><DataQuality /></ProtectedRoute>} />
      <Route path="/hrms/assistant" element={<ProtectedRoute><HrmsAssistant /></ProtectedRoute>} />
      <Route path="/hrms/dashboard" element={<ProtectedRoute><HrmsDashboard /></ProtectedRoute>} />
      <Route path="/hrms/company-documents" element={<ProtectedRoute><CompanyDocuments /></ProtectedRoute>} />
    </Routes>
  );
}

const GLOBAL_CSS = `
  html {
    --bg: #f0f4fc; --surface: #ffffff; --surface-2: #f4f8ff; --surface2: #f4f8ff;
    --panel: #f8faff; --card: #ffffff;
    --border: #c0d0eb; --border-2: #d5e3f5; --border2: #d5e3f5;
    --accent: #2563eb; --violet: #6d5ef5;
    --text: #0d1a36; --text-2: #2a4a78; --text2: #2a4a78;
    --muted: #6080aa;
    color-scheme: light;
  }
  body { background: #f0f4fc; color: #0d1a36; }
`;

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <style>{GLOBAL_CSS}</style>
        <AuthProvider>
          <BillingStatusProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </BillingStatusProvider>
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
